import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Plus, Shield, Trash2, Archive } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1557264337-e8a93017fe92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxkZWVwJTIwc3BhY2UlMjBkYXJrfGVufDB8fHx8MTc3NzU5ODA5Mnww&ixlib=rb-4.1.0&q=85";

export default function Campaigns() {
  const { user } = useAuth();
  const [camps, setCamps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", game_type: "Campaign" });

  const load = async () => { try { const r = await api.get("/campaigns"); setCamps(r.data); } catch { toast.error("Failed to load"); } };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/campaigns", form); toast.success("Campaign created. You are the GM."); setOpen(false); setForm({ name: "", description: "", game_type: "Campaign" }); load(); }
    catch { toast.error("Create failed"); }
  };

  const remove = async (e, id, name) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`DELETE campaign "${name}"?\n\nThis permanently erases:\n• The campaign record\n• All sessions & chat history\n• Custom campaign questions\n\nCharacters will be unassigned (kept).\n\nThis CANNOT be undone.`)) return;
    if (!window.confirm(`Final confirmation: really delete "${name}"?`)) return;
    try { await api.delete(`/campaigns/${id}`); toast.success("Campaign deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const endCampaign = async (e, id, name) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`End campaign "${name}"?\n\nThe campaign will become READ-ONLY — no new sessions, edits, purchases or chat. History is preserved. An admin can still delete it if needed.`)) return;
    try { await api.post(`/campaigns/${id}/end`); toast.success("Campaign ended"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const isAdmin = user?.role === "admin";
  const active = camps.filter(c => !c.ended);
  const visible = isAdmin ? active : active.filter(c => c.gm_id === user?.id || (c.party_ids || []).includes(user?.id));
  const endedCount = camps.filter(c => c.ended && (isAdmin || c.gm_id === user?.id || (c.party_ids || []).includes(user?.id))).length;

  return (
    <div className="p-4 sm:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-mono text-3xl sm:text-4xl amber amber-glow uppercase tracking-tight">// Campaigns</h1>
          <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-[0.2em] uppercase">
            {isAdmin ? "// Where good plans go to die" : "// Where good plans go to die"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {endedCount > 0 && (
            <Link to="/campaigns/archive" className="btn-amber flex items-center gap-2" data-testid="goto-archive">
              <Archive size={14} strokeWidth={1.5} /> Archive ({endedCount})
            </Link>
          )}
          <button className="btn-amber flex items-center gap-2" data-testid="new-campaign-btn" onClick={() => setOpen(true)}>
            <Plus size={14} strokeWidth={1.5} /> New Campaign
          </button>
        </div>
      </div>

      {open && (
        <form onSubmit={create} className="panel p-6 mb-8 space-y-4" data-testid="campaign-create-form">
          <div><label className="label-term mb-1 block">Name</label><input data-testid="camp-name-input" className="input-term" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required /></div>
          <div><label className="label-term mb-1 block">Game Type</label>
            <select className="input-term" data-testid="camp-gametype-input" value={form.game_type} onChange={e=>setForm({...form,game_type:e.target.value})}>
              <option value="Campaign">Campaign</option>
              <option value="Cinematic">Cinematic</option>
            </select>
          </div>
          <div><label className="label-term mb-1 block">Description (Markdown supported)</label><textarea className="input-term min-h-[80px]" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} /></div>
          <div className="flex gap-2"><button className="btn-amber" data-testid="camp-create-submit">Create</button><button type="button" className="btn-amber" onClick={()=>setOpen(false)}>Cancel</button></div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="campaigns-grid">
        {visible.length === 0 && <div className="mono text-[#A0A0A0]">// No campaigns for you yet. Create one or ask a GM to add your character.</div>}
        {visible.map(c => {
          const canDelete = isAdmin;
          const canEnd = !c.ended && (isAdmin || c.gm_id === user?.id);
          return (
            <Link key={c.id} to={`/campaigns/${c.id}`} data-testid={`campaign-card-${c.id}`} className={`panel p-0 overflow-hidden hover:shadow-[0_0_18px_rgba(255,176,0,0.3)] transition-all relative ${c.ended ? "opacity-70" : ""}`}>
              <div className="h-32 bg-cover bg-center opacity-60 relative" style={{ backgroundImage: `url(${c.image_path ? fileUrl(c.image_path) : BG})`, filter: "grayscale(0.5) hue-rotate(-15deg)" }}>
                {c.image_path && <div className="absolute bottom-2 right-2 w-16 h-16 rounded-full overflow-hidden border-2 border-[#FFB000]"><img src={fileUrl(c.image_path)} alt="" className="w-full h-full object-cover" /></div>}
                {c.ended && <div className="absolute top-2 left-2 mono text-[10px] bg-[#FF2A2A] text-black px-2 py-0.5 tracking-widest">CAMPAIGN ENDED</div>}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <Shield size={14} strokeWidth={1.5} className="amber" />
                  <div className="heading-mono text-lg amber uppercase flex-1 truncate">{c.name}</div>
                  {canEnd && (
                    <button onClick={(e) => endCampaign(e, c.id, c.name)} className="text-xs mono amber border border-[rgba(255,176,0,0.4)] px-2 py-1 hover:bg-[rgba(255,176,0,0.1)]" data-testid={`end-campaign-${c.id}`} title="End Campaign">End</button>
                  )}
                  {canDelete && (
                    <button onClick={(e) => remove(e, c.id, c.name)} className="text-[#FF2A2A] p-1 hover:bg-[rgba(255,42,42,0.1)]" data-testid={`delete-campaign-${c.id}`} title="Delete (admin only)">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="mono text-xs text-[#A0A0A0] mt-1">{c.game_type}</div>
                <div className="flex items-center justify-between mt-3 mono text-[10px]">
                  <span className="text-[#A0A0A0]">Party: {c.party_ids?.length || 0}</span>
                  {c.ended ? <span className="text-[#FF2A2A]">● ENDED</span> :
                    <span className={c.session_active ? "neon-green green-glow" : "text-[#A0A0A0]"}>{c.session_active ? "● SESSION LIVE" : "○ IDLE"}</span>}
                  {c.gm_id === user?.id && <span className="amber">GM</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
